// pages/reports/IncomeReport.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { 
    ChevronUp, 
    RotateCcw, 
    Download,
    Printer,
    DollarSign,
    TrendingUp,
    PieChart,
    FileText,
    Calendar,
    ShoppingBag,
    CreditCard,
    RefreshCw,
   
    Layers
} from 'feather-icons-react/build/IconComponents';
import { reportActions } from '../../core/action/reportActions';

const MySwal = withReactContent(Swal);

const IncomeReport = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);
    
    // State
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fromDate, setFromDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [toDate, setToDate] = useState(new Date());
    const [activeTab, setActiveTab] = useState('summary');
    const [sourceFilter, setSourceFilter] = useState('all');
    
    // Format date for API
    const formatDateForAPI = (date) => {
        return date.toISOString().split('T')[0];
    };
    
    // Load report data
    const loadReport = useCallback(async () => {
        try {
            setLoading(true);
            
            const params = {
                from_date: formatDateForAPI(fromDate),
                to_date: formatDateForAPI(toDate)
            };
            
            const result = await reportActions.getIncomeReport(params);
            
            if (result.success) {
                setReportData(result.data);
            } else {
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: result.message || 'Failed to load report',
                    timer: 3000,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error('Error loading report:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load report',
                timer: 3000,
                showConfirmButton: false
            });
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate]);
    
    // Load on mount and when dependencies change
    useEffect(() => {
        loadReport();
    }, [loadReport]);
    
    // Handle export PDF
    const handleExportPDF = async () => {
        try {
            const params = {
                from_date: formatDateForAPI(fromDate),
                to_date: formatDateForAPI(toDate)
            };
            
            const result = await reportActions.exportToPDF('income', params);
            
            if (result) {
                const url = window.URL.createObjectURL(new Blob([result.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `income_report_${formatDateForAPI(fromDate)}_to_${formatDateForAPI(toDate)}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            console.error('Error exporting PDF:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to export PDF',
                timer: 2000,
                showConfirmButton: false
            });
        }
    };
    
    // Handle export Excel
    const handleExportExcel = async () => {
        try {
            const params = {
                from_date: formatDateForAPI(fromDate),
                to_date: formatDateForAPI(toDate)
            };
            
            const result = await reportActions.exportToExcel('income', params);
            
            if (result) {
                const url = window.URL.createObjectURL(new Blob([result.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `income_report_${formatDateForAPI(fromDate)}_to_${formatDateForAPI(toDate)}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            console.error('Error exporting Excel:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to export Excel',
                timer: 2000,
                showConfirmButton: false
            });
        }
    };
    
    // Handle print
    const handlePrint = () => {
        window.print();
    };
    
    // Filter data by source
    const getFilteredSources = () => {
        if (!reportData?.source_breakdown) return [];
        if (sourceFilter === 'all') return reportData.source_breakdown;
        return reportData.source_breakdown.filter(s => 
            s.source.toLowerCase().includes(sourceFilter.toLowerCase())
        );
    };
    
    // Render filter bar
    const renderFilterBar = () => {
        const sourceOptions = [
            { value: 'all', label: 'All Sources' },
            { value: 'Sales', label: 'Sales Income' },
            { value: 'Other', label: 'Other Income' }
        ];
        
        return (
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-3">
                            <label className="form-label">From Date</label>
                            <DatePicker
                                selected={fromDate}
                                onChange={setFromDate}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                maxDate={toDate}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">To Date</label>
                            <DatePicker
                                selected={toDate}
                                onChange={setToDate}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                minDate={fromDate}
                                maxDate={new Date()}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Income Source</label>
                            <Select
                                options={sourceOptions}
                                value={sourceOptions.find(opt => opt.value === sourceFilter)}
                                onChange={(opt) => setSourceFilter(opt?.value || 'all')}
                                placeholder="Select source"
                            />
                        </div>
                        <div className="col-md-3 d-flex align-items-end">
                            <button className="btn btn-primary w-100" onClick={loadReport}>
                                <RefreshCw size={16} className="me-2" />
                                Generate
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render summary cards
    const renderSummaryCards = () => {
        if (!reportData) return null;
        
        const { summary } = reportData;
        
        return (
            <div className="row mb-4">
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Income</p>
                                    <h3 className="mb-0 text-success">
                                        {reportActions.formatCurrency(summary.total_income)}
                                    </h3>
                                    <small className="text-muted">All sources</small>
                                </div>
                                <div className="bg-success bg-opacity-10 rounded-circle p-3">
                                    <TrendingUp size={24} className="text-success" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Sales Income</p>
                                    <h3 className="mb-0 text-primary">
                                        {reportActions.formatCurrency(summary.sales_income)}
                                    </h3>
                                    <small className="text-muted">{summary.sales_transactions} transactions</small>
                                </div>
                                <div className="bg-primary bg-opacity-10 rounded-circle p-3">
                                    <ShoppingBag size={24} className="text-primary" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Other Income</p>
                                    <h3 className="mb-0 text-info">
                                        {reportActions.formatCurrency(summary.other_income)}
                                    </h3>
                                    <small className="text-muted">{summary.other_transactions} transactions</small>
                                </div>
                                <div className="bg-info bg-opacity-10 rounded-circle p-3">
                                    <Layers size={24} className="text-info" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Amount Received</p>
                                    <h3 className="mb-0 text-warning">
                                        {reportActions.formatCurrency(summary.total_received)}
                                    </h3>
                                    <small className="text-muted">Collected</small>
                                </div>
                                <div className="bg-warning bg-opacity-10 rounded-circle p-3">
                                    <DollarSign size={24} className="text-warning" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render tabs
    const renderTabs = () => {
        const tabs = [
            { id: 'summary', label: 'Summary', icon: <PieChart size={16} /> },
            { id: 'daily', label: 'Daily Breakdown', icon: <Calendar size={16} /> },
            { id: 'sources', label: 'Income Sources', icon: <Layers size={16} /> },
            { id: 'payments', label: 'Payment Methods', icon: <CreditCard size={16} /> }
        ];
        
        return (
            <ul className="nav nav-tabs mb-4">
                {tabs.map(tab => (
                    <li className="nav-item" key={tab.id}>
                        <button
                            className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.icon}
                            <span className="ms-2">{tab.label}</span>
                        </button>
                    </li>
                ))}
            </ul>
        );
    };
    
    // Render summary tab
    const renderSummaryTab = () => {
        if (!reportData) return null;
        
        const { summary } = reportData;
        
        return (
            <div className="row">
                <div className="col-md-6 mb-4">
                    <div className="card h-100">
                        <div className="card-header">
                            <h6 className="mb-0">Income Overview</h6>
                        </div>
                        <div className="card-body">
                            <table className="table table-sm">
                                <tbody>
                                    <tr className="table-success">
                                        <td><strong>Total Income</strong></td>
                                        <td className="text-end"><strong>{reportActions.formatCurrency(summary.total_income)}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>Sales Income</td>
                                        <td className="text-end">{reportActions.formatCurrency(summary.sales_income)}</td>
                                    </tr>
                                    <tr>
                                        <td>Other Income</td>
                                        <td className="text-end">{reportActions.formatCurrency(summary.other_income)}</td>
                                    </tr>
                                    <tr>
                                        <td>Amount Received (Cash/Bank)</td>
                                        <td className="text-end text-success">{reportActions.formatCurrency(summary.total_received)}</td>
                                    </tr>
                                    <tr>
                                        <td>Amount Receivable</td>
                                        <td className="text-end text-warning">{reportActions.formatCurrency(summary.total_receivable)}</td>
                                    </tr>
                                    <tr>
                                        <td>Collection Ratio</td>
                                        <td className="text-end">
                                            {summary.total_income > 0 
                                                ? ((summary.total_received / summary.total_income) * 100).toFixed(2) + '%'
                                                : '0%'}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                
                <div className="col-md-6 mb-4">
                    <div className="card h-100">
                        <div className="card-header">
                            <h6 className="mb-0">Income Distribution</h6>
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <div className="d-flex justify-content-between mb-1">
                                    <span>Sales Income</span>
                                    <span className="fw-bold">{reportActions.formatCurrency(summary.sales_income)}</span>
                                </div>
                                <div className="progress" style={{ height: '10px' }}>
                                    <div 
                                        className="progress-bar bg-primary" 
                                        style={{ width: `${(summary.sales_income / summary.total_income) * 100}%` }}
                                    ></div>
                                </div>
                                <small className="text-muted">
                                    {((summary.sales_income / summary.total_income) * 100).toFixed(1)}% of total
                                </small>
                            </div>
                            
                            <div>
                                <div className="d-flex justify-content-between mb-1">
                                    <span>Other Income</span>
                                    <span className="fw-bold">{reportActions.formatCurrency(summary.other_income)}</span>
                                </div>
                                <div className="progress" style={{ height: '10px' }}>
                                    <div 
                                        className="progress-bar bg-info" 
                                        style={{ width: `${(summary.other_income / summary.total_income) * 100}%` }}
                                    ></div>
                                </div>
                                <small className="text-muted">
                                    {((summary.other_income / summary.total_income) * 100).toFixed(1)}% of total
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render daily tab
    const renderDailyTab = () => {
        if (!reportData?.daily_breakdown || reportData.daily_breakdown.length === 0) {
            return (
                <div className="alert alert-info">
                    No daily income data available for the selected period.
                </div>
            );
        }
        
        // Group by date
        const dailyData = {};
        reportData.daily_breakdown.forEach(item => {
            if (!dailyData[item.date]) {
                dailyData[item.date] = {
                    date: item.date,
                    sales_count: 0,
                    sales_amount: 0,
                    other_count: 0,
                    other_amount: 0,
                    total: 0
                };
            }
            if (item.source === 'sales') {
                dailyData[item.date].sales_count += item.transaction_count;
                dailyData[item.date].sales_amount += item.amount;
            } else {
                dailyData[item.date].other_count += item.transaction_count;
                dailyData[item.date].other_amount += item.amount;
            }
            dailyData[item.date].total += item.amount;
        });
        
        const dailyList = Object.values(dailyData).sort((a, b) => 
            new Date(a.date) - new Date(b.date)
        );
        
        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Daily Income Breakdown</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Date</th>
                                    <th className="text-end">Sales Count</th>
                                    <th className="text-end">Sales Amount</th>
                                    <th className="text-end">Other Count</th>
                                    <th className="text-end">Other Amount</th>
                                    <th className="text-end">Total Income</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dailyList.map((day, idx) => (
                                    <tr key={idx}>
                                        <td>{reportActions.formatDate(day.date)}</td>
                                        <td className="text-end">{day.sales_count}</td>
                                        <td className="text-end text-primary">{reportActions.formatCurrency(day.sales_amount)}</td>
                                        <td className="text-end">{day.other_count}</td>
                                        <td className="text-end text-info">{reportActions.formatCurrency(day.other_amount)}</td>
                                        <td className="text-end text-success fw-bold">{reportActions.formatCurrency(day.total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="table-light">
                                <tr>
                                    <th>Total</th>
                                    <th className="text-end">
                                        {dailyList.reduce((sum, d) => sum + d.sales_count, 0)}
                                    </th>
                                    <th className="text-end">
                                        {reportActions.formatCurrency(dailyList.reduce((sum, d) => sum + d.sales_amount, 0))}
                                    </th>
                                    <th className="text-end">
                                        {dailyList.reduce((sum, d) => sum + d.other_count, 0)}
                                    </th>
                                    <th className="text-end">
                                        {reportActions.formatCurrency(dailyList.reduce((sum, d) => sum + d.other_amount, 0))}
                                    </th>
                                    <th className="text-end text-success">
                                        {reportActions.formatCurrency(dailyList.reduce((sum, d) => sum + d.total, 0))}
                                    </th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render sources tab
    const renderSourcesTab = () => {
        if (!reportData?.source_breakdown || reportData.source_breakdown.length === 0) {
            return (
                <div className="alert alert-info">
                    No income source data available.
                </div>
            );
        }
        
        const filteredSources = getFilteredSources();
        
        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Income by Source</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Source</th>
                                    <th className="text-end">Transactions</th>
                                    <th className="text-end">Total Amount</th>
                                    <th className="text-end">Percentage</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSources.map((source, idx) => {
                                    const percentage = (source.total_amount / reportData.summary.total_income) * 100;
                                    return (
                                        <tr key={idx}>
                                            <td>
                                                <strong>{source.source}</strong>
                                            </td>
                                            <td className="text-end">{source.transaction_count}</td>
                                            <td className="text-end text-success">{reportActions.formatCurrency(source.total_amount)}</td>
                                            <td className="text-end">
                                                <div className="d-flex align-items-center justify-content-end">
                                                    <span className="me-2">{percentage.toFixed(2)}%</span>
                                                    <div style={{ width: '50px' }}>
                                                        <div className="progress" style={{ height: '5px' }}>
                                                            <div 
                                                                className="progress-bar bg-success" 
                                                                style={{ width: `${percentage}%` }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render payments tab
    const renderPaymentsTab = () => {
        if (!reportData?.payment_breakdown || reportData.payment_breakdown.length === 0) {
            return (
                <div className="alert alert-info">
                    No payment method data available.
                </div>
            );
        }
        
        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Payment Method Breakdown</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Source</th>
                                    <th>Payment Method</th>
                                    <th className="text-end">Transactions</th>
                                    <th className="text-end">Total Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.payment_breakdown.map((payment, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <span className="badge bg-secondary">{payment.source}</span>
                                        </td>
                                        <td>
                                            <span className="badge bg-info">
                                                {payment.payment_method?.replace('_', ' ')}
                                            </span>
                                        </td>
                                        <td className="text-end">{payment.transaction_count}</td>
                                        <td className="text-end text-success">{reportActions.formatCurrency(payment.total_amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    // Tooltip renderer
    const renderTooltip = useCallback((props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    ), []);
    
    if (loading && !reportData) {
        return (
            <div className="page-wrapper">
                <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <h5 className="text-muted">Loading Income Report...</h5>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="add-item d-flex">
                        <div className="page-title">
                            <h4>Income Report</h4>
                            <h6>Track all income sources and revenue</h6>
                        </div>
                    </div>
                    
                    <ul className="table-top-head">
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export PDF')}>
                                <button className="btn btn-outline-danger" onClick={handleExportPDF}>
                                    <Download size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export Excel')}>
                                <button className="btn btn-outline-success" onClick={handleExportExcel}>
                                    <FileText size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Print')}>
                                <button className="btn btn-outline-secondary" onClick={handlePrint}>
                                    <Printer size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <button className="btn btn-outline-secondary" onClick={loadReport}>
                                    <RotateCcw size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <button
                                    className={data ? "btn btn-outline-secondary active" : "btn btn-outline-secondary"}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>
                
                {/* Period Info */}
                <div className="row mb-3">
                    <div className="col-md-12">
                        <div className="card bg-light">
                            <div className="card-body py-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>Period:</strong> {reportActions.formatDate(reportData?.period.from_date)} to {reportActions.formatDate(reportData?.period.to_date)}
                                    </div>
                                    <div>
                                        <strong>Total Income:</strong> {reportActions.formatCurrency(reportData?.summary.total_income || 0)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Filter Bar */}
                {renderFilterBar()}
                
                {/* Summary Cards */}
                {renderSummaryCards()}
                
                {/* Tabs */}
                {renderTabs()}
                
                {/* Tab Content */}
                {activeTab === 'summary' && renderSummaryTab()}
                {activeTab === 'daily' && renderDailyTab()}
                {activeTab === 'sources' && renderSourcesTab()}
                {activeTab === 'payments' && renderPaymentsTab()}
            </div>
        </div>
    );
};

export default IncomeReport;